import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { db } from '../../firebase/config';
import { doc, getDoc } from 'firebase/firestore';
import { getClasificacionChip } from '../../utils/styles';

/**
 * ModalFichaCliente - Centro de información 360 del cliente con lógica de prioridad.
 * Prioriza los nuevos arrays de strings sobre los campos legacy para evitar duplicidad visual.
 */
const ModalFichaCliente = ({ show, onClose, client }) => {
    const [fullClient, setFullClient] = useState(null);
    const [contadorFull, setContadorFull] = useState(null);
    const [loading, setLoading] = useState(true);

    const clientId = client?.id;

    // --- EFFECT: CARGAR DATOS EXTENDIDOS DEL CLIENTE Y CONTADOR ---
    useEffect(() => {
        if (!show || !clientId) return;

        const fetchData = async () => {
            setLoading(true);
            try {
                const clientSnap = await getDoc(doc(db, 'public/data/clientes', clientId));
                if (clientSnap.exists()) {
                    const clientData = { id: clientSnap.id, ...clientSnap.data() };
                    setFullClient(clientData);

                    // Carga de datos del estudio contable si existe ID
                    if (clientData.tieneContador && clientData.contadorId) {
                        const contSnap = await getDoc(doc(db, 'public/data/contadores', clientData.contadorId));
                        if (contSnap.exists()) {
                            setContadorFull({ id: contSnap.id, ...contSnap.data() });
                        }
                    }
                }
            } catch (error) {
                console.error("Error al cargar ficha técnica:", error);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [show, clientId]);

    if (!show) return null;

    // --- HELPERS ---
    
    const formatPhoneNumber = (v) => {
        return v || '-';
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        if (typeof dateString === 'string' && dateString.includes('-')) {
            const parts = dateString.split('-');
            if (parts.length === 3) return `${parts[2]}/${parts[1]}/${parts[0]}`;
        }
        return dateString;
    };

    const DetailItem = ({ label, value, highlight = false }) => (
        <div className="text-left">
            <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1 text-left">{label}</p>
            <div className={`text-sm font-medium break-words min-h-[1.5rem] text-left ${highlight ? 'text-blue-700 font-bold' : 'text-gray-800 uppercase'}`}>
                {value || <span className="text-gray-300 italic font-normal text-xs">Sin dato</span>}
            </div>
        </div>
    );

    const ContactCard = ({ name, role, email, phone, roleColor = 'orange' }) => {
        if (!name && !email && !phone) return null;
        
        const badgeStyles = {
            orange: "bg-orange-100 text-orange-700 border-orange-200",
            blue: "bg-indigo-50 text-indigo-700 border-indigo-100"
        };

        return (
            <div className="bg-gray-50/50 border border-gray-200 rounded-2xl p-4 shadow-sm space-y-2 mt-2 max-w-md animate-fade-in text-left">
                <div className="flex items-center gap-2 text-left">
                    <span className="font-black text-gray-800 uppercase text-sm">{name}</span>
                    {role && (
                        <span className={`px-2 py-0.5 rounded-md text-[9px] font-black uppercase border ${badgeStyles[roleColor]}`}>
                            {role}
                        </span>
                    )}
                </div>
                <div className="space-y-1 text-left">
                    {email && (
                        <div className="flex items-center gap-2 text-gray-500 text-xs font-medium">
                            <svg className="w-3.5 h-3.5 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2"><path d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 00-2-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
                            <span className="lowercase">{email}</span>
                        </div>
                    )}
                    {phone && (
                        <div className="flex items-center gap-2 text-gray-500 text-xs font-medium">
                            <svg className="w-3.5 h-3.5 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2"><path d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" /></svg>
                            {formatPhoneNumber(phone)}
                        </div>
                    )}
                </div>
            </div>
        );
    };

    const currentClient = fullClient || client;

    // --- LÓGICA DE PRIORIDAD (COEXISTENCIA SIN DUPLICADOS) ---
    
    // EMAILS: Filtramos el array de strings planos
    const validEmailsArray = (Array.isArray(currentClient?.emails) ? currentClient.emails : [])
        .filter(e => e && typeof e === 'string' && e.trim() !== '');

    // Prioridad: Si el array tiene datos, usamos el array. Si no, usamos el campo plano legacy.
    const displayEmails = validEmailsArray.length > 0 
        ? validEmailsArray 
        : (currentClient?.email ? [currentClient.email] : []);

    // TELÉFONOS: Filtramos el array de strings planos
    const validTelefonosArray = (Array.isArray(currentClient?.telefonos) ? currentClient.telefonos : [])
        .filter(t => t && typeof t === 'string' && t.trim() !== '');

    // Prioridad: Si el array tiene datos, usamos el array. Si no, usamos el campo plano legacy.
    const displayTelefonos = validTelefonosArray.length > 0 
        ? validTelefonosArray 
        : (currentClient?.telefono ? [currentClient.telefono] : []);

    return createPortal(
        <div className="fixed inset-0 bg-gray-900 bg-opacity-60 z-[1100] flex items-center justify-center p-4 backdrop-blur-sm transition-opacity text-left font-['Inter']">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl h-auto max-h-[90vh] flex flex-col overflow-hidden animate-fade-in border border-gray-100 text-left">
                
                {/* Header */}
                <div className="flex justify-between items-center px-8 py-5 border-b border-gray-100 shrink-0 bg-white text-left">
                    <div className="text-left">
                        <h3 className="text-2xl font-bold text-gray-800 text-left uppercase tracking-tight">Ficha Técnica de Cliente</h3>
                        <div className="mt-2 flex items-center gap-3 text-left">
                            <span className="bg-gray-100 px-3 py-1 rounded-md border border-gray-200 font-mono font-bold text-gray-800 text-lg text-left uppercase">
                                {currentClient?.nombre} {currentClient?.apellido || ''}
                            </span>
                            {currentClient?.clasificacion && (
                                <span className={`font-bold px-3 py-1 rounded-md text-sm uppercase border ${getClasificacionChip(currentClient.clasificacion)}`}>
                                    {currentClient.clasificacion}
                                </span>
                            )}
                        </div>
                    </div>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors p-2 rounded-full hover:bg-gray-50">
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path></svg>
                    </button>
                </div>

                {/* Content */}
                <div className="overflow-y-auto p-8 flex-grow bg-white space-y-8 text-left custom-scrollbar">
                    {loading ? (
                        <div className="p-20 text-center flex flex-col items-center gap-4">
                            <div className="w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                            <p className="text-xs font-black text-slate-400 uppercase tracking-widest animate-pulse">Cargando legajo completo...</p>
                        </div>
                    ) : (
                        <>
                            {/* 1. DATOS DE IDENTIDAD */}
                            <fieldset className="border border-gray-200 rounded-xl p-6 relative mt-2 text-left">
                                <legend className="px-2 text-lg font-bold text-gray-700 bg-white absolute -top-3 left-4">Datos de Identidad</legend>
                                <div className="grid grid-cols-1 md:grid-cols-4 gap-6 pt-2 text-left">
                                    <DetailItem label="Persona" value={currentClient.persona} />
                                    <DetailItem label="DNI" value={currentClient.dni} />
                                    <DetailItem label="CUIT" value={currentClient.cuit} />
                                    <DetailItem label="Condición Fiscal" value={currentClient.condicionFiscal} />
                                </div>
                            </fieldset>

                            {/* 2. ESTRUCTURA COMERCIAL */}
                            <fieldset className="border border-gray-200 rounded-xl p-6 relative mt-4 text-left">
                                <legend className="px-2 text-lg font-bold text-gray-700 bg-white absolute -top-3 left-4 text-left">Estructura Comercial</legend>
                                <div className="space-y-6 pt-2 text-left">
                                    <div className="grid grid-cols-1 md:grid-cols-4 gap-6 text-left">
                                        <DetailItem label="Tipo de Canal" value={currentClient.canal} />
                                        <DetailItem label="Nombre del Canal" value={currentClient.canalNombre} />
                                        <DetailItem label="Subcanal / Asociado" value={currentClient.subcanalNombre} />
                                        <DetailItem label="¿Contador Asociado?" value={currentClient.tieneContador ? `SÍ (${currentClient.contadorNombre})` : 'NO POSEE'} />
                                    </div>

                                    {/* Contactos del Estudio Contable */}
                                    {contadorFull?.asociados && contadorFull.asociados.length > 0 && (
                                        <div className="pt-4 border-t border-gray-100 text-left">
                                            <div className="flex items-center gap-2 mb-3">
                                                <div className="w-1.5 h-1.5 rounded-full bg-orange-500"></div>
                                                <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest text-left">Contactos del Estudio Contable</p>
                                            </div>
                                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 text-left">
                                                {contadorFull.asociados.map((asoc, idx) => (
                                                    <ContactCard 
                                                        key={idx}
                                                        name={`${asoc.nombre} ${asoc.apellido}`}
                                                        role={asoc.cargo}
                                                        email={asoc.email}
                                                        phone={asoc.telefono}
                                                        roleColor="orange"
                                                    />
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </fieldset>

                            {/* 3. INFORMACIÓN DE CONTACTO - CON PRIORIDAD */}
                            <fieldset className="border border-gray-200 rounded-xl p-6 relative mt-4 text-left">
                                <legend className="px-2 text-lg font-bold text-gray-700 bg-white absolute -top-3 left-4 text-left">Información de Contacto</legend>
                                <div className="space-y-8 pt-2 text-left">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-10 text-left">
                                        <div>
                                            <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3 text-left">Correos Electrónicos</p>
                                            <div className="space-y-2 text-left">
                                                {displayEmails.length > 0 ? displayEmails.map((email, idx) => (
                                                    <div key={idx} className="flex items-center text-gray-800 font-bold text-sm bg-slate-50 p-2 rounded-lg border border-slate-100 text-left">
                                                        <span className="w-2 h-2 bg-blue-400 rounded-full mr-2"></span>
                                                        <span className="lowercase">{email}</span>
                                                    </div>
                                                )) : <span className="text-gray-300 italic text-sm">Sin datos</span>}
                                            </div>
                                        </div>
                                        <div>
                                            <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3 text-left">Teléfonos</p>
                                            <div className="space-y-2 text-left">
                                                {displayTelefonos.length > 0 ? displayTelefonos.map((tel, idx) => (
                                                    <div key={idx} className="flex items-center text-gray-800 font-bold text-sm bg-slate-50 p-2 rounded-lg border border-slate-100 text-left">
                                                        <span className="w-2 h-2 bg-emerald-400 rounded-full mr-2"></span>
                                                        {formatPhoneNumber(tel)}
                                                    </div>
                                                )) : <span className="text-gray-300 italic text-sm">Sin datos</span>}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Contactos Adicionales */}
                                    {currentClient.contactosAdicionales && currentClient.contactosAdicionales.length > 0 && (
                                        <div className="pt-4 border-t border-gray-100 text-left">
                                            <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3 text-left">Contactos Adicionales</p>
                                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 text-left">
                                                {currentClient.contactosAdicionales.map((contacto, idx) => (
                                                    <ContactCard 
                                                        key={idx}
                                                        name={`${contacto.nombre} ${contacto.apellido}`}
                                                        role={contacto.cargo}
                                                        email={contacto.email}
                                                        phone={contacto.telefono}
                                                        roleColor="blue"
                                                    />
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </fieldset>

                            {/* 4. DOMICILIO */}
                            <fieldset className="border border-gray-200 rounded-xl p-6 relative mt-4 text-left">
                                <legend className="px-2 text-lg font-bold text-gray-700 bg-white absolute -top-3 left-4 text-left">Domicilio</legend>
                                <div className="grid grid-cols-1 md:grid-cols-4 gap-6 pt-2 text-left">
                                    <div className="md:col-span-2 text-left">
                                        <DetailItem label="Calle / Dirección" value={`${currentClient.domicilio || ''} ${currentClient.numero || currentClient.domicilio_numero || ''} ${currentClient.piso ? 'Piso ' + currentClient.piso : ''} ${currentClient.departamento ? 'Depto ' + currentClient.departamento : ''}`} />
                                    </div>
                                    <DetailItem label="C.P." value={currentClient.codigoPostal} />
                                    <DetailItem label="Localidad" value={currentClient.localidad} />
                                    <DetailItem label="Provincia" value={currentClient.provincia} />
                                </div>
                            </fieldset>

                            {/* 5. OBSERVACIONES */}
                            <fieldset className="border border-orange-200 rounded-xl p-6 relative mt-4 bg-orange-50/20 text-left">
                                <legend className="px-2 text-lg font-bold text-orange-700 bg-white rounded-lg absolute -top-3 left-4 text-left text-xs uppercase tracking-widest">
                                    Observaciones Generales
                                </legend>
                                <div className="pt-2 text-left">
                                    <div className="bg-white/80 p-4 rounded-lg border border-orange-200 min-h-[5rem] text-left">
                                        <p className="text-gray-700 whitespace-pre-wrap text-sm text-left leading-relaxed">
                                            {currentClient.observaciones || <span className="text-gray-300 italic">No se registran comentarios adicionales en la ficha del cliente.</span>}
                                        </p>
                                    </div>
                                </div>
                            </fieldset>
                        </>
                    )}
                </div>

                {/* Footer */}
                <div className="px-8 py-5 border-t border-gray-100 flex justify-end bg-gray-50 shrink-0 text-left">
                    <button onClick={onClose} className="px-8 py-2.5 rounded-full bg-white border border-gray-300 text-gray-700 font-bold hover:bg-gray-100 transition shadow-sm text-left uppercase text-xs tracking-widest active:scale-95">
                        Cerrar Ficha
                    </button>
                </div>
            </div>

            <style>{`
                .custom-scrollbar::-webkit-scrollbar { width: 6px; }
                .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
                .custom-scrollbar::-webkit-scrollbar-thumb { background: #e5e7eb; border-radius: 10px; }
                .animate-fade-in { animation: fadeIn 0.3s ease-out forwards; }
                @keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
            `}</style>
        </div>,
        document.body
    );
};

export default ModalFichaCliente;